import "dayjs/locale/pt";
import "dayjs/locale/pt-br";
import "dayjs/locale/es";
import "dayjs/locale/es-do";
import "dayjs/locale/es-mx";
import "dayjs/locale/es-pr";
import "dayjs/locale/en";
import "dayjs/locale/en-au";
import "dayjs/locale/en-ca";
import "dayjs/locale/en-ie";
import "dayjs/locale/en-il";
import "dayjs/locale/en-in";
import "dayjs/locale/en-nz";
import "dayjs/locale/en-sg";
import "dayjs/locale/en-tt";
import "dayjs/locale/he";
import React, { ComponentType, Suspense, useEffect } from "react";
import "./App.scss";
import "./i18n/config";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Layout, LayoutWithNav } from "./pages/Layout/Layout";
import { DependencyInjectionProvider, useDependencyInjector } from "@whatslly/commons/di";
import { container } from "./services";
import { injectSVG } from "@whatslly/ui-components/useSVG";
import { ModalProvider } from "@whatslly/ui-components/modals";
import "virtual:svg-icons-register";
import { useDeprecatedGlobalStyles } from "@whatslly/ui-components/styles/useTheme";
import { observer } from "mobx-react-lite";
import { JumpPage } from "./pages/Jump/JumpPage";
import { useTranslation } from "react-i18next";
import { NavMenuStore } from "./pages/Layout/NavMenuStore";
import { getSupportedDayjsLocale } from "@whatslly/commons/browserUtils";
import dayjs from "dayjs";
import { LoadingPage } from "./components/LoadingPage";
import { SearchPreservingNavigate } from "./components/SearchPreservingNavigate/SearchPreservingNavigate";

const locale = getSupportedDayjsLocale();
dayjs.locale(locale);

type Route<C extends string, T extends ComponentType<any>> = () => Promise<Record<C, T> | Record<string, any>>;
const LazyLoadedRoute = React.memo(<C extends string, T extends ComponentType<any>>({ route, exported }: { route: Route<C, T>; exported: C }) => {
  const Factory: any = React.lazy(async () => ({ default: (await route())[exported] }));
  return (
    <Suspense fallback={<LoadingPage />}>
      <Factory />
    </Suspense>
  );
});

const ChannelsPage = <LazyLoadedRoute route={() => import("./pages/ChannelsPage")} exported="ChannelsPage" />;
const WhatsAppBusinessApiSettingsPage = (
  <LazyLoadedRoute
    route={() => import("./pages/TuvisMessaging/settings/WhatsAppBusinessApiSettingsPage")}
    exported="WhatsAppBusinessApiSettingsPage"
  />
);
const SlackSettingsPage = (
  <LazyLoadedRoute route={() => import("./pages/TuvisMessaging/platforms/slack/SlackSettingsPage")} exported="SlackSettingsPage" />
);
const TeamsSettingsPage = (
  <LazyLoadedRoute route={() => import("./pages/TuvisMessaging/platforms/teams/TeamsSettingsPage")} exported="TeamsSettingsPage" />
);
const ArchiveTeamsSettingsPage = (
  <LazyLoadedRoute
    route={() => import("./pages/TuvisMessaging/platforms/archive-teams/ArchiveTeamsSettingsPage")}
    exported="ArchiveTeamsSettingsPage"
  />
);
const ArchiveDiscordSettingsPage = (
  <LazyLoadedRoute
    route={() => import("./pages/TuvisMessaging/platforms/archive-discord/ArchiveDiscordSettingsPage")}
    exported="ArchiveDiscordSettingsPage"
  />
);
const TwilioSettingsPage = (
  <LazyLoadedRoute route={() => import("./pages/TuvisMessaging/settings/Twilio/TwilioSettingsPage")} exported="TwilioSettingsPage" />
);
const TuvisMessaging = <LazyLoadedRoute route={() => import("./pages/TuvisMessaging/agent")} exported="TuvisMessaging" />;

const LoginPage = <LazyLoadedRoute route={() => import("./pages/Layout/LoginPage")} exported="Page" />;
const Unauthorized = <LazyLoadedRoute route={() => import("./pages/Unauthorized/Unauthorized")} exported="Unauthorized" />;
const SamlLogin = <LazyLoadedRoute route={() => import("./pages/Layout/SamlLogin")} exported="SAMLLoginWrapper" />;
const SalesforceLogin = <LazyLoadedRoute route={() => import("./pages/Layout/PlatformSalesforceLogin")} exported="PlatformSalesforceLogin" />;
const MicrosoftDynamicsLogin = (
  <LazyLoadedRoute route={() => import("./pages/Layout/PlatformMicrosoftDynamicsLogin")} exported="PlatformMicrosoftDynamicsLogin" />
);
const HomePage = <LazyLoadedRoute route={() => import("./pages/HomePage/HomePage")} exported="HomePage" />;
const AnalyticsDashboard = <LazyLoadedRoute route={() => import("./pages/AnalyticsDashboard/AnalyticsDashboard")} exported="AnalyticsDashboard" />;
const OrgOverviewPage = <LazyLoadedRoute route={() => import("./pages/AdminTools/OrgOverview/OrgOverview")} exported="OrgOverview" />;
const AccountOverviewPage = <LazyLoadedRoute route={() => import("./pages/Account/AccountOverview/AccountOverview")} exported="AccountOverview" />;
const AccountUsersPage = <LazyLoadedRoute route={() => import("./pages/Account/AccountUsers/AccountUsersPage")} exported="AccountUsersPage" />;
const KeywordInsightsCampaignsPage = (
  <LazyLoadedRoute route={() => import("./pages/KeywordInsights/KeywordInsightsCampaignsPage")} exported="KeywordInsightsCampaignsPage" />
);
const KeywordInsightsOccurrencesPage = (
  <LazyLoadedRoute route={() => import("./pages/KeywordInsights/KeywordInsightsOccurrencesPage")} exported="KeywordInsightsOccurrencesPage" />
);
const KeywordAnalyticsPage = <LazyLoadedRoute route={() => import("./pages/KeywordInsights/KeywordAnalyticsPage")} exported="KeywordAnalyticsPage" />;
const KeywordInsightsOccurrencePage = (
  <LazyLoadedRoute route={() => import("./pages/KeywordInsights/KeywordInsightsOccurrencePage")} exported="KeywordInsightsOccurrencePage" />
);
const DLPAnalyticsPage = <LazyLoadedRoute route={() => import("./pages/Dlp/DLPAnalyticsPage")} exported="DLPAnalyticsPage" />;
const SurveillanceAnalyticsPage = (
  <LazyLoadedRoute route={() => import("./pages/Surveillance/SurveillanceAnalyticsPage")} exported="SurveillanceAnalyticsPage" />
);
const ChannelsAnalyticsPage = <LazyLoadedRoute route={() => import("./pages/ChannelsPage/ChannelsAnalyticsPage")} exported="ChannelsAnalyticsPage" />;
const Adoption = <LazyLoadedRoute route={() => import("./pages/Adoption/Adoption")} exported="Adoption" />;
const SidecarsPage = <LazyLoadedRoute route={() => import("./pages/Sidecars/SidecarsPage")} exported="SidecarsPage" />;
const SidecarsInvitePage = <LazyLoadedRoute route={() => import("./pages/Sidecars/SidecarsPage")} exported="SidecarsPair" />;
const ReInviteSidecar = <LazyLoadedRoute route={() => import("./pages/Sidecars/SidecarsPage")} exported="ReInviteSidecarPage" />;
const UsersPage = <LazyLoadedRoute route={() => import("./pages/UsersPage/endUsers")} exported="UsersPage" />;
const UserPage = <LazyLoadedRoute route={() => import("./pages/UsersPage/endUsers")} exported="UserPage" />;
const AuditLogPage = <LazyLoadedRoute route={() => import("./pages/AuditLog/AuditLogPage")} exported="AuditLogPage" />;
const SamlPage = <LazyLoadedRoute route={() => import("./pages/Saml/SamlPage")} exported="SamlPage" />;
const DestinationsPage = <LazyLoadedRoute route={() => import("./pages/Destinations/DestinationsPage")} exported="DestinationsPage" />;
const DestinationPage = <LazyLoadedRoute route={() => import("./pages/Destinations/DestinationPage")} exported="DestinationPage" />;
const DLPPoliciesPage = <LazyLoadedRoute route={() => import("./pages/Dlp/DLPPoliciesPage/DLPPoliciesPage")} exported="DLPPoliciesPage" />;
const DLPPolicyBuilderPage = (
  <LazyLoadedRoute route={() => import("./pages/Dlp/DLPPolicyBuilderPage/DLPPolicyBuilderPage")} exported="DLPPolicyBuilderPage" />
);
const DLPRulesPage = <LazyLoadedRoute route={() => import("./pages/Dlp/DLPRulesPage/DLPRulesPage")} exported="DLPRulesPage" />;
const DLPIncidentsPage = <LazyLoadedRoute route={() => import("./pages/Dlp/DLPIncidents/DLPIncidentsPage")} exported="DLPIncidentsPage" />;
const DLPIncidentPage = <LazyLoadedRoute route={() => import("./pages/Dlp/DLPIncidentPage")} exported="DLPIncidentPage" />;
const SurveillancePoliciesPage = (
  <LazyLoadedRoute
    route={() => import("./pages/Surveillance/SurveillancePoliciesPage/SurveillancePoliciesPage")}
    exported="SurveillancePoliciesPage"
  />
);
const SurveillancePolicyBuilderPage = (
  <LazyLoadedRoute
    route={() => import("./pages/Surveillance/SurveillancePolicyBuilderPage/SurveillancePolicyBuilderPage")}
    exported="SurveillancePolicyBuilderPage"
  />
);
const SurveillanceRulesPage = (
  <LazyLoadedRoute route={() => import("./pages/Surveillance/SurveillanceRulesPage/SurveillanceRulesPage")} exported="SurveillanceRulesPage" />
);
const SurveillanceIncidentsPage = (
  <LazyLoadedRoute
    route={() => import("./pages/Surveillance/SurveillanceIncidents/SurveillanceIncidentsPage")}
    exported="SurveillanceIncidentsPage"
  />
);
const SurveillanceIncidentPage = (
  <LazyLoadedRoute route={() => import("./pages/Surveillance/SurveillanceIncidentPage")} exported="SurveillanceIncidentPage" />
);
const FraudDetectionPage = <LazyLoadedRoute route={() => import("./pages/FraudDetection/FraudDetectionPage")} exported="FraudDetectionPage" />;
const FraudDetectionHashesPage = (
  <LazyLoadedRoute route={() => import("./pages/FraudDetection/FraudDetectionHashesPage")} exported="FraudDetectionHashesPage" />
);
const FraudDetectionUrlsPage = (
  <LazyLoadedRoute route={() => import("./pages/FraudDetection/FraudDetectionUrlsPage")} exported="FraudDetectionUrlsPage" />
);
const FraudDetectionIncidentsPage = (
  <LazyLoadedRoute route={() => import("./pages/FraudDetection/Incidents/FraudDetectionIncidentsPage")} exported="FraudDetectionIncidentsPage" />
);
const FraudDetectionIncidentPage = (
  <LazyLoadedRoute route={() => import("./pages/FraudDetection/Incidents/FraudDetectionIncidentPage")} exported="FraudDetectionIncidentPage" />
);
const NotificationsPage = <LazyLoadedRoute route={() => import("./pages/Settings/NotificationsPage")} exported="NotificationsPage" />;
const GlobalLayoutPage = <LazyLoadedRoute route={() => import("./pages/Settings/GlobalLayoutPage")} exported="GlobalLayoutPage" />;
const ApiKeysPage = <LazyLoadedRoute route={() => import("./pages/Settings/ApiKeysPage")} exported="ApiKeysPage" />;
const EliteTokenPage = <LazyLoadedRoute route={() => import("./pages/Settings/EliteTokenPage")} exported="EliteTokenPage" />;
const PhoneWhitelistPage = <LazyLoadedRoute route={() => import("./pages/Settings/PhoneWhitelistPage")} exported="PhoneWhitelistPage" />;
const PhoneBlocklistPage = <LazyLoadedRoute route={() => import("./pages/Settings/PhoneBlocklistPage")} exported="PhoneBlocklistPage" />;
const IncidentSummaryEmail = <LazyLoadedRoute route={() => import("./pages/Settings/IncidentSummaryEmail")} exported="IncidentSummaryEmail" />;
const SuperAdminOrgConfigsPage = (
  <LazyLoadedRoute route={() => import("./pages/SuperAdminOrgConfigs/SuperAdminOrgConfigsPage")} exported="SuperAdminOrgConfigsPage" />
);
const OrgExplorerPage = <LazyLoadedRoute route={() => import("./pages/AdminTools/OrgExplorer/OrgExplorerPage")} exported="OrgExplorerPage" />;

const UrlPoliciesListPage = <LazyLoadedRoute route={() => import("./pages/UrlPolicies/UrlPoliciesListPage")} exported="UrlPoliciesListPage" />;
const UrlPolicyWizardPage = <LazyLoadedRoute route={() => import("./pages/UrlPolicies/UrlPolicyWizardPage")} exported="UrlPolicyWizardPage" />;
const TuvisContactsPage = <LazyLoadedRoute route={() => import("./pages/TuvisContacts/TuvisContactsPage")} exported="TuvisContactsPage" />;
const DashboardsListPage = <LazyLoadedRoute route={() => import("./pages/Dashboards/DashboardsListPage")} exported="DashboardsListPage" />;
const DashboardBuilderPage = <LazyLoadedRoute route={() => import("./pages/Dashboards/DashboardBuilderPage")} exported="DashboardBuilderPage" />;
const CustomFiltersListPage = (
  <LazyLoadedRoute route={() => import("./pages/CustomFilters/CustomFiltersListPage")} exported="CustomFiltersListPage" />
);

const SidecarNotificationsPage = (
  <LazyLoadedRoute route={() => import("./pages/Settings/SidecarNotificationsPage")} exported="SidecarNotificationsPage" />
);
const SidecarCustomizationsPage = (
  <LazyLoadedRoute route={() => import("./pages/Settings/SidecarCustomizationsPage")} exported="SidecarCustomizationsPage" />
);
const InstallDesktopPage = <LazyLoadedRoute route={() => import("./pages/InstallDesktop")} exported="InstallDesktopPage" />;
const UserGroupsPage = <LazyLoadedRoute route={() => import("./pages/UserGroups/UserGroupsPage")} exported="UserGroupsPage" />;
const UserGroupPage = <LazyLoadedRoute route={() => import("./pages/UserGroups/UserGroupPage")} exported="UserGroupPage" />;
const GroupMetadataPages = <LazyLoadedRoute route={() => import("./pages/GroupMetadata/GroupMetadataPages")} exported="GroupMetadataPages" />;
const GroupMetadataPage = <LazyLoadedRoute route={() => import("./pages/GroupMetadata/GroupMetadataPage")} exported="GroupMetadataPage" />;
const AllUsersPage = <LazyLoadedRoute route={() => import("./pages/AllUsers/AllUsersPage")} exported="AllUsersPage" />;
const SfEmailDomainMapPage = <LazyLoadedRoute route={() => import("./pages/Settings/SfEmailDomainMapPage")} exported="SfEmailDomainMapPage" />;
const MessagesAnalyticsPage = (
  <LazyLoadedRoute route={() => import("./pages/MessagesAnalytics/MessagesAnalyticsPage")} exported="MessagesAnalyticsPage" />
);
const BotBuilderPage = <LazyLoadedRoute route={() => import("./pages/BotBuilder/BotBuilderPage")} exported="BotBuilderPage" />;
const BotFlowEditorPage = <LazyLoadedRoute route={() => import("./pages/BotBuilder/BotFlowEditorPage")} exported="BotFlowEditorPage" />;
const BotCompareVersionsPage = (
  <LazyLoadedRoute route={() => import("./pages/BotBuilder/BotCompareVersionsPage")} exported="BotCompareVersionsPage" />
);
const BotAnalyticsDashboard = <LazyLoadedRoute route={() => import("./pages/BotBuilder/BotAnalyticsDashboard")} exported="BotAnalyticsDashboard" />;
const BotCredentialsPage = <LazyLoadedRoute route={() => import("./pages/BotBuilder/BotCredentialsPage")} exported="BotCredentialsPage" />;
const BotSessionsPage = <LazyLoadedRoute route={() => import("./pages/BotBuilder/BotSessionsPage")} exported="BotSessionsPage" />;
const BotSessionDebugPage = <LazyLoadedRoute route={() => import("./pages/BotBuilder/BotSessionDebugPage")} exported="BotSessionDebugPage" />;
const BotSettingsPage = <LazyLoadedRoute route={() => import("./pages/BotBuilder/BotSettingsPage")} exported="BotSettingsPage" />;
const TriggerBatchPage = <LazyLoadedRoute route={() => import("./pages/BotBuilder/TriggerBatch/TriggerBatchPage")} exported="TriggerBatchPage" />;
const TriggerBatchesPage = (
  <LazyLoadedRoute route={() => import("./pages/BotBuilder/TriggerBatch/TriggerBatchesPage")} exported="TriggerBatchesPage" />
);
const CampaignsTablePage = <LazyLoadedRoute route={() => import("./pages/Campaigns/CampaignsTablePage")} exported="CampaignsTablePage" />;
const CampaignsTemplatesPage = <LazyLoadedRoute route={() => import("./pages/Campaigns/CampaignsTemplatesPage")} exported="CampaignsTemplatesPage" />;

injectSVG();
// eslint-disable-next-line react-hooks/rules-of-hooks
useDeprecatedGlobalStyles({});

let baseUrl: string = import.meta.env.VITE_BASE_URL?.toString() ?? "";
if (baseUrl.endsWith("/")) {
  baseUrl = baseUrl.slice(0, -1);
}

const AppRoutes = observer(() => {
  const navMenuStore = useDependencyInjector(NavMenuStore);
  const { t, i18n } = useTranslation();
  useEffect(() => navMenuStore.init(t), [t, i18n.language, navMenuStore]);

  return (
    <BrowserRouter basename={baseUrl}>
      <Routes>
        <Route path="/login" element={Unauthorized}>
          <Route path="salesforce" element={SalesforceLogin} />
          <Route path="microsoft_dynamics" element={MicrosoftDynamicsLogin} />
          <Route path="saml" element={SamlLogin} />
          <Route path="jump" element={<JumpPage />} />
          <Route index element={LoginPage} />
        </Route>
        <Route path="/chat/sidecars/invite/:sidecarId" element={SidecarsInvitePage} />
        <Route path="/chat/agent/*" element={TuvisMessaging} />
        <Route path="/install/desktop" element={<Layout>{InstallDesktopPage}</Layout>} />
        <Route
          path="/settings"
          element={
            <LayoutWithNav navigationOptions={navMenuStore.tree.dict["/settings"]?.subtree?.items ?? []} title={t("settings")} parentUrl="/" />
          }
        >
          <Route path="notifications" element={NotificationsPage} />
          <Route path="api-keys" element={ApiKeysPage} />
          <Route path="elite-token" element={EliteTokenPage} />
          <Route path="incidents-summary-email" element={IncidentSummaryEmail} />
          <Route path="global-layout" element={GlobalLayoutPage} />
          <Route path="saml" element={SamlPage} />
          <Route path="sf-email-domain-map" element={SfEmailDomainMapPage} />
          <Route path="org-configs/:category?" element={SuperAdminOrgConfigsPage} />
          <Route path="phones-whitelist" element={PhoneWhitelistPage} />
          <Route path="phones-blocklist" element={PhoneBlocklistPage} />
          <Route path="dashboards">
            <Route path=":dashboardId/edit" element={DashboardBuilderPage} />
            <Route index element={DashboardsListPage} />
          </Route>
          <Route path="custom-filters" element={CustomFiltersListPage} />
          <Route index element={<Navigate to={navMenuStore.tree.dict["/settings"]?.subtree?.defaultUrl ?? navMenuStore.tree.defaultUrl} replace />} />
        </Route>
        <Route
          path="/bots"
          element={
            <LayoutWithNav navigationOptions={navMenuStore.tree.dict["/bots"]?.subtree?.items ?? []} title={t("bots")} parentUrl="/" disablePadding />
          }
        >
          <Route path="analytics" element={BotAnalyticsDashboard} />
          <Route path="credentials" element={BotCredentialsPage} />
          <Route path="settings" element={BotSettingsPage} />
          <Route path="sessions" element={<Outlet />}>
            <Route path=":sessionId" element={BotSessionDebugPage} />
            <Route index element={BotSessionsPage} />
          </Route>
          <Route path="triggers" element={<Outlet />}>
            <Route path=":batchId" element={TriggerBatchPage} />
            <Route index element={TriggerBatchesPage} />
          </Route>
          <Route path="flows" element={<Outlet />}>
            <Route path=":flowId/compare/:range" element={BotCompareVersionsPage} />
            <Route path=":flowId" element={BotFlowEditorPage} />
            <Route index element={BotBuilderPage} />
          </Route>
          <Route index element={<Navigate to={navMenuStore.tree.dict["/bots"]?.subtree?.defaultUrl ?? navMenuStore.tree.defaultUrl} replace />} />
        </Route>
        <Route
          path="/account"
          element={
            <LayoutWithNav
              navigationOptions={navMenuStore.tree.dict["/account"]?.subtree?.items ?? []}
              title={t("account_admin.title")}
              parentUrl="/"
            />
          }
        >
          <Route path="overview" element={AccountOverviewPage} />
          <Route path="users" element={AccountUsersPage} />
          <Route index element={<Navigate to={navMenuStore.tree.dict["/account"]?.subtree?.defaultUrl ?? navMenuStore.tree.defaultUrl} replace />} />
        </Route>
        <Route
          path="/channels"
          element={
            <LayoutWithNav
              disablePadding
              navigationOptions={navMenuStore.tree.dict["/channels"]?.subtree?.items ?? []}
              title={t("channels.title")}
              parentUrl="/"
            />
          }
        >
          <Route path="mobile">
            <Route index element={SidecarsPage} />
            <Route path="re-invite/:sidecarId" element={ReInviteSidecar} />
            <Route path=":channel" element={SidecarsPage} />
          </Route>
          <Route path="sidecar-notifications" element={SidecarNotificationsPage} />
          <Route path="sidecar-customizations" element={SidecarCustomizationsPage} />
          <Route path="analytics" element={ChannelsAnalyticsPage} />
          <Route index element={ChannelsPage} />
        </Route>
        <Route
          path="/campaigns"
          element={
            <LayoutWithNav
              navigationOptions={navMenuStore.tree.dict["/campaigns"]?.subtree?.items ?? []}
              title={t("campaigns.title")}
              parentUrl="/"
            />
          }
        >
          <Route path="list" element={CampaignsTablePage} />
          <Route path="templates" element={CampaignsTemplatesPage} />
          <Route
            index
            element={<SearchPreservingNavigate to={navMenuStore.tree.dict["/campaigns"]?.subtree?.items?.[0]?.to ?? "/campaigns/list"} replace />}
          />
        </Route>
        <Route
          path="/analytics/dashboard"
          element={
            <LayoutWithNav
              disablePadding
              navigationOptions={navMenuStore.tree.dict["/analytics/dashboard"]?.subtree?.items ?? []}
              title={t("analytics_dashboard.title")}
              parentUrl="/"
            />
          }
        >
          <Route
            index
            element={<Navigate to={navMenuStore.tree.dict["/analytics/dashboard"]?.subtree?.defaultUrl ?? navMenuStore.tree.defaultUrl} replace />}
          />
          <Route path=":tab" element={AnalyticsDashboard} />
        </Route>
        <Route
          path="/chat/destinations"
          element={
            <LayoutWithNav
              disablePadding
              navigationOptions={navMenuStore.tree.dict["/chat/destinations"]?.subtree?.items ?? []}
              title={t("destinations_page.title")}
              parentUrl="/"
            />
          }
        >
          <Route
            index
            element={<Navigate to={navMenuStore.tree.dict["/chat/destinations"]?.subtree?.defaultUrl ?? navMenuStore.tree.defaultUrl} replace />}
          />
          <Route path="integration">
            <Route index element={<SearchPreservingNavigate to="messages" replace />} />
            <Route path="waba" element={WhatsAppBusinessApiSettingsPage} />
            <Route path="slack" element={SlackSettingsPage} />
            <Route path="teams" element={TeamsSettingsPage} />
            <Route path="tuvis-call-recording" element={TwilioSettingsPage} />
            <Route path="archive/teams" element={ArchiveTeamsSettingsPage} />
            <Route path="archive/discord" element={ArchiveDiscordSettingsPage} />
            <Route path=":tab" element={DestinationsPage} />
          </Route>
          <Route path=":id" element={DestinationPage} />
        </Route>
        <Route
          path="/dlp"
          element={
            <LayoutWithNav
              disablePadding
              navigationOptions={navMenuStore.tree.dict["/dlp"]?.subtree?.items ?? []}
              title={t("dlp_page.title")}
              parentUrl="/"
            />
          }
        >
          <Route index element={<Navigate to={navMenuStore.tree.dict["/dlp"]?.subtree?.defaultUrl ?? navMenuStore.tree.defaultUrl} replace />} />
          <Route path="analytics" element={DLPAnalyticsPage} />
          <Route path="policies/new" element={DLPPolicyBuilderPage} />
          <Route path="policies/:policyId" element={DLPPolicyBuilderPage} />
          <Route path="policies" element={DLPPoliciesPage} />
          <Route path="rules" element={DLPRulesPage} />
          <Route path="incidents" element={<Outlet />}>
            <Route index element={DLPIncidentsPage} />
          </Route>
          <Route path="incidents/:incidentId" element={DLPIncidentPage} />
        </Route>
        <Route
          path="/surveillance"
          element={
            <LayoutWithNav
              disablePadding
              navigationOptions={navMenuStore.tree.dict["/surveillance"]?.subtree?.items ?? []}
              title={t("surveillance_page.title")}
              parentUrl="/"
            />
          }
        >
          <Route
            index
            element={<Navigate to={navMenuStore.tree.dict["/surveillance"]?.subtree?.defaultUrl ?? navMenuStore.tree.defaultUrl} replace />}
          />
          <Route path="analytics" element={SurveillanceAnalyticsPage} />
          <Route path="policies/new" element={SurveillancePolicyBuilderPage} />
          <Route path="policies/:policyId" element={SurveillancePolicyBuilderPage} />
          <Route path="policies" element={SurveillancePoliciesPage} />
          <Route path="rules" element={SurveillanceRulesPage} />
          <Route path="incidents" element={<Outlet />}>
            <Route index element={SurveillanceIncidentsPage} />
          </Route>
          <Route path="incidents/:incidentId" element={SurveillanceIncidentPage} />
        </Route>
        <Route
          path="/keyword-insights"
          element={
            <LayoutWithNav
              disablePadding
              navigationOptions={navMenuStore.tree.dict["/keyword-insights"]?.subtree?.items ?? []}
              title={t("keyword_insights.title")}
              parentUrl="/"
            />
          }
        >
          <Route
            index
            element={<Navigate to={navMenuStore.tree.dict["/keyword-insights"]?.subtree?.defaultUrl ?? navMenuStore.tree.defaultUrl} replace />}
          />
          <Route path="campaigns" element={KeywordInsightsCampaignsPage} />
          <Route path="occurrences">
            <Route index element={KeywordInsightsOccurrencesPage} />
            <Route path="occurrence/:occurrenceId" element={KeywordInsightsOccurrencePage} />
          </Route>
          <Route path="analytics" element={KeywordAnalyticsPage} />
        </Route>
        <Route
          path="/fraud-detection"
          element={
            <LayoutWithNav
              disablePadding
              navigationOptions={navMenuStore.tree.dict["/fraud-detection"]?.subtree?.items ?? []}
              title={t("fraud_detection_page.title")}
              parentUrl="/"
            />
          }
        >
          <Route
            index
            element={<Navigate to={navMenuStore.tree.dict["/fraud-detection"]?.subtree?.defaultUrl ?? navMenuStore.tree.defaultUrl} replace />}
          />
          <Route element={FraudDetectionPage}>
            <Route path="hashes" element={FraudDetectionHashesPage} />
            <Route path="urls" element={FraudDetectionUrlsPage} />
            <Route path="incidents" element={<Outlet />}>
              <Route index element={FraudDetectionIncidentsPage} />
            </Route>
          </Route>
          <Route path="incidents/:incidentId" element={FraudDetectionIncidentPage} />
        </Route>
        <Route path="*" element={<LayoutWithNav navigationOptions={navMenuStore.tree.items} disablePadding />}>
          <Route path="home" element={HomePage} />
          <Route path="analytics" element={<Outlet />}>
            <Route path="adoption" element={Adoption} />
            <Route path="messages" element={MessagesAnalyticsPage} />
          </Route>
          <Route path="admin" element={<Outlet />}>
            <Route path="users">
              <Route path=":userId" element={UserPage} />
              <Route index element={UsersPage} />
            </Route>
            <Route path="tools">
              <Route path="org-explorer" element={OrgExplorerPage} />
              <Route path="org-overview" element={OrgOverviewPage} />
            </Route>
            <Route path="groups">
              <Route path=":groupId" element={UserGroupPage} />
              <Route index element={UserGroupsPage} />
            </Route>
            <Route path="all-users" element={AllUsersPage} />
            <Route path="audit-log" element={AuditLogPage} />
          </Route>
          <Route path="groups-metadata">
            <Route path=":groupId" element={GroupMetadataPage} />
            <Route index element={GroupMetadataPages} />
          </Route>
          <Route path="desktop-policies" element={<Outlet />}>
            <Route index element={UrlPoliciesListPage} />
            <Route path="new" element={UrlPolicyWizardPage} />
            <Route path=":policyId/edit" element={UrlPolicyWizardPage} />
          </Route>
          <Route path="tuvis-contacts" element={TuvisContactsPage} />
          <Route path={"overview"} element={OrgOverviewPage} />
        </Route>
        <Route path="*" element={<LayoutWithNav navigationOptions={navMenuStore.tree.items} title={t("tuvis_platform")} />}>
          <Route path="*" element={<Navigate to={navMenuStore.tree.defaultUrl} replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
});

export const App: React.FC = () => {
  return (
    <React.StrictMode>
      <DependencyInjectionProvider container={container}>
        <ModalProvider />
        <AppRoutes />
      </DependencyInjectionProvider>
    </React.StrictMode>
  );
};
